import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Typography, Alert } from '@one-thd/sui-atomic-components';
import {
  CustomerAccountNavigation,
  useAccountCrumbs,
} from '@thd-olt-component-react/customer-account-navigation';
import { MilitaryVerification } from '@thd-olt-component-react/military-verification';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import FAQs from '../components/common/FAQs';
import ProgramSectionContainer from '../components/ProgramSection/ProgramSectionContainer';
import { getSvocId, getUserId, redirectToSignIn } from '../utils/utils';
import { ROUTES } from '../constants/constants';
import { Verification } from '../components/Verification';
import { getEnrollmentStatus } from '../api/api';
import { mapCrumbsToStaticCrumbs } from '../utils/breadcrumbs';
import { track, lifeCycleTrack } from '../analytics/analytics';
import { PAGE_LOAD, GET_STARTED_LINK } from '../constants/analytics';
import {
  GENERAL_ERROR_MSG,
  REDIRECT_ERROR_MSG,
} from '../constants/alert-messages';
import { SectionSkeleton } from '../components/common/Loaders/Loaders';
import { MilitaryVerificationErrorProvider } from '../contexts/MilitaryVerificationErrorContext';
import { MilitaryVerificationErrorBanner } from '../components/common/MilitaryVerificationErrorBanner';
import {
  MilitaryVerificationSuccessProvider,
  useMilitaryVerificationSuccess,
} from '../contexts/MilitaryVerificationSuccessContext';
import { MilitaryVerificationSuccessToast } from '../components/common/MilitaryVerificationSuccessToast';

const crumbs = [
  {
    label: ROUTES.landing.label,
    path: ROUTES.landing.url,
  },
];

export const LandingPage = ({ customerAccountId, userId }) => {
  return (
    <MilitaryVerificationErrorProvider>
      <MilitaryVerificationSuccessProvider>
        <QueryProvider cacheKey="military-discount-landing-page">
          <LandingPageContent
            customerAccountId={customerAccountId}
            userId={userId}
          />
        </QueryProvider>
      </MilitaryVerificationSuccessProvider>
    </MilitaryVerificationErrorProvider>
  );
};

LandingPage.propTypes = {
  customerAccountId: PropTypes.string,
  userId: PropTypes.string,
};
LandingPage.defaultProps = {};
LandingPage.displayName = 'LandingPage';

const LandingPageContent = ({ customerAccountId, userId }) => {
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [showIframe, setShowIframe] = useState(false);
  const header = showIframe ? 'Verification' : 'Military Discount Benefit';
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const staticCrumbs = mapCrumbsToStaticCrumbs(useAccountCrumbs(crumbs));
  const [isSpouse, setIsSpouse] = useState(false);
  const { verifiedProfile } = useMilitaryVerificationSuccess();

  /* The enrollment API is the system of record, but it lags: enrollment is a
  /* Loyalty concept updated asynchronously off the ID.me write via CDC, so a
  /* customer who verified seconds ago still reads as 'N' here and refetching
  /* would not change that. A verification this page saw for itself is the only
  /* thing that covers the gap, so it counts alongside the API rather than
  /* waiting on it.
  /*
  /* Resolved once, here, and handed to everything that renders off it - the
  /* program section and the FAQs both key on enrollment, and a page that worked
  /* this out in two places would end up congratulating the customer above a set
  /* of instructions for enrolling. */
  const isEnrolled = apiResponse?.enrollmentStatus === 'Y' || !!verifiedProfile;

  const fetchCustomerEnrollment = async () => {
    setIsLoading(true);
    if (!getUserId() || !getSvocId()) {
      setApiError({ showRedirectAlert: true });
      redirectToSignIn();
      return;
    }

    return getEnrollmentStatus(channel).then(function (resData) {
      const response = resData?.response;
      const error = resData?.error;

      if (response) {
        setApiResponse(response?.data?.militaryDiscount);
        setApiError(false);
        setIsLoading(false);
        if (
          response?.data?.militaryDiscount?.verificationId &&
          response?.data?.militaryDiscount?.enrollmentStatus === 'N'
        ) {
          setShowIframe(true);
        }
      }
      setIsLoading(false);
      if (
        error?.response?.status === 401 ||
        (error?.response?.status === 400 &&
          error?.response?.data?.errors[0]?.errorCode === 'AUTH_ERR_101') ||
        error?.response?.status === 403
      ) {
        setApiError({ showRedirectAlert: true });
        redirectToSignIn();
      } else if (error) {
        setApiError(error);
        throw error;
      }

      return resData;
    });
  };

  useEffect(() => {
    fetchCustomerEnrollment()
      .then((resData) => {
        const response = resData?.response;
        if (response) {
          lifeCycleTrack(PAGE_LOAD, {
            isEnrolled:
              response?.data?.militaryDiscount?.enrollmentStatus === 'Y',
            hasMaxDiscount:
              response?.data?.militaryDiscount?.isMilitaryDiscountCapped,
          });
        }
      })
      .catch((error) => {
        if (error && !apiError?.showRedirectAlert) {
          setApiError({
            generalError: true,
          });
        }
      });
  }, []);

  /* This function will call the fetchCustomerEnrollment service to make
  /* sure user's session is still valid. If the session has expired, function in Axios Client
  /* will redirect user to Sign In page. If session is valid but APi returns any error, we will ignore it
  /* because that is irrelevant for the registration process. */

  const verifyOnClick = async (spouseFlag) => {
    track(GET_STARTED_LINK);
    setIsSpouse(spouseFlag);
    await fetchCustomerEnrollment().then(function () {
      setShowIframe(true);
    });
  };

  const pageContent = showIframe ? (
    <Verification apiResponse={apiResponse} isSpouse={isSpouse} />
  ) : (
    <>
      <div className="sui-grid sui-gap-3 sui-py-3">
        {!isConsumerApp && (
          <ErrorBoundary name="breadcrumbs">
            <Breadcrumbs staticCrumbs={staticCrumbs} />
          </ErrorBoundary>
        )}
        {!isMobile && (
          <Typography
            data-testid="page-header"
            weight="display"
            variant="h1"
            uppercase
          >
            {header}
          </Typography>
        )}
      </div>
      <div className="sui-grid sui-grid-cols-12 sui-gap-5 sui-py-3">
        <div
          className="sui-col-span-12 lg:sui-col-span-3"
          data-testid="left-nav"
        >
          <ErrorBoundary name="customer-account-navigation">
            <CustomerAccountNavigation />
          </ErrorBoundary>
        </div>

        <div className="sui-col-span-12 lg:sui-col-span-9">
          {/* Suppressed on a general enrollment-status failure so it doesn't stack with GENERAL_ERROR_MSG below. */}
          {!apiError?.generalError && <MilitaryVerificationErrorBanner />}
          {apiError?.generalError ? (
            <Alert status="error">{GENERAL_ERROR_MSG}</Alert>
          ) : (
            <>
              <div className="sui-mb-4">
                <Typography variant="h2">
                  Our Thanks for Your Service
                </Typography>
                <Typography>
                  Receive{' '}
                  <Typography component="span" weight="bold">
                    10% off
                  </Typography>{' '}
                  eligible purchases* - every day, year round - if you&apos;re
                  an active service member, veteran or military spouse.
                </Typography>
              </div>
              {isLoading && <SectionSkeleton section="program" />}
              <ErrorBoundary name="program-section-container">
                <ProgramSectionContainer
                  apiResponse={apiResponse}
                  apiError={apiError}
                  isEnrolled={isEnrolled}
                  verifyOnClick={verifyOnClick}
                  customerAccountId={customerAccountId}
                  userId={userId}
                />
              </ErrorBoundary>
              <div className="sui-mb-4 sui-mt-16">
                <Typography variant="h2">FAQs</Typography>
              </div>
              {isLoading && <SectionSkeleton section="FAQ" />}
              <ErrorBoundary name="faqs">
                <FAQs apiResponse={apiResponse} isEnrolled={isEnrolled} />
              </ErrorBoundary>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="container">
      <div className="grid isBound">
        <div className="col__12-12 u--fix-960">
          {apiError?.showRedirectAlert ? (
            <div className="sui-mt-8">
              <Alert status="info">{REDIRECT_ERROR_MSG}</Alert>
            </div>
          ) : (
            pageContent
          )}
        </div>
      </div>
      {/* Deliberately outside the view switch above, and outside the column the
          page renders into: the toast has to survive the re-render that takes
          the verify button off the page, and it pins itself to the bottom of the
          viewport rather than to whatever contains it. */}
      <MilitaryVerificationSuccessToast />
    </div>
  );
};

LandingPageContent.propTypes = {
  customerAccountId: PropTypes.string,
  userId: PropTypes.string,
};

LandingPageContent.dataModel = extend(
  CustomerAccountNavigation,
  Breadcrumbs,
  MilitaryVerification,
);

LandingPageContent.displayName = 'LandingPageContent';
